import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

const NAVY_MID  = '#132233';
const AMBER     = '#F5A623';
const FONT_BODY = "'DM Sans', sans-serif";

// Reusable top-nav dropdown: a parent tab-bar button that reveals a small menu
// of child tabs. Opens on hover or click, closes on outside click / Escape,
// and is keyboard-navigable (Enter/Space/ArrowDown to open, arrow keys to move
// between items). Matches the existing flat tab buttons' sizing/typography so
// it sits inline with them without any visual seam.
//
// The menu panel is rendered through a portal into document.body, positioned via
// the trigger's own bounding rect, rather than as a normal absolutely-positioned
// child -- the tab bar it lives in scrolls horizontally (overflow-x: auto), and
// setting only one overflow axis forces the browser to compute the other as
// 'auto' too, which would otherwise clip the panel to the bar's ~46px height.
export default function NavDropdown({ label, active, items }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const closeTimer = useRef(null);
  // A real mouse click always fires mouseenter immediately beforehand, so
  // onClick's toggle would otherwise race the hover-open and instantly re-close
  // it. Once mouseenter has taken over for a mouse user, click is a no-op --
  // hover governs open/close for them. Keyboard activation (Enter/Space, no
  // pointer movement) never sets this, so it keeps working as a real toggle.
  const isHoveringRef = useRef(false);

  const clearCloseTimer = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  };
  const openNow = () => { clearCloseTimer(); setOpen(true); };
  const closeSoon = () => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };
  const closeNow = () => { clearCloseTimer(); setOpen(false); };

  useLayoutEffect(() => {
    if (!open) return;
    const updateCoords = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) setCoords({ top: rect.bottom, left: rect.left });
    };
    updateCoords();
    window.addEventListener('resize', updateCoords);
    window.addEventListener('scroll', updateCoords, true);
    return () => {
      window.removeEventListener('resize', updateCoords);
      window.removeEventListener('scroll', updateCoords, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e) => {
      const insideTrigger = triggerRef.current && triggerRef.current.contains(e.target);
      const insidePanel = panelRef.current && panelRef.current.contains(e.target);
      if (!insideTrigger && !insidePanel) {
        if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  useEffect(() => () => clearCloseTimer(), []);

  const focusItem = (index) => {
    const els = panelRef.current?.querySelectorAll('[role="menuitem"]');
    if (els && els[index]) els[index].focus();
  };

  const handleButtonKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(v => !v);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      openNow();
      setTimeout(() => focusItem(0), 0);
    } else if (e.key === 'Escape') {
      closeNow();
    }
  };

  const handleItemKeyDown = (e, index) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusItem(index + 1 < items.length ? index + 1 : 0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusItem(index > 0 ? index - 1 : items.length - 1);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeNow();
      triggerRef.current?.focus();
    }
  };

  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => { if (!isHoveringRef.current) setOpen(v => !v); }}
        onKeyDown={handleButtonKeyDown}
        onMouseEnter={() => { isHoveringRef.current = true; openNow(); }}
        onMouseLeave={() => { isHoveringRef.current = false; closeSoon(); }}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          padding: '12px 18px', fontSize: '0.86rem', fontWeight: 600,
          fontFamily: FONT_BODY, whiteSpace: 'nowrap', flexShrink: 0,
          color: active ? AMBER : 'rgba(255,255,255,0.4)',
          borderBottom: active ? `2px solid ${AMBER}` : '2px solid transparent',
          marginBottom: '-1px', position: 'relative',
          display: 'flex', alignItems: 'center', gap: '5px',
        }}
      >
        {label}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
        {items.some(i => i.badge) && (
          <span style={{ position: 'absolute', top: '8px', right: '6px', width: '6px', height: '6px', borderRadius: '50%', backgroundColor: AMBER }} />
        )}
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          role="menu"
          onMouseEnter={openNow}
          onMouseLeave={closeSoon}
          style={{
            position: 'fixed', top: coords.top, left: coords.left, zIndex: 1000,
            backgroundColor: NAVY_MID, border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px', padding: '6px', minWidth: '180px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
          }}
        >
          {items.map((item, i) => (
            <button
              key={item.label}
              role="menuitem"
              tabIndex={0}
              onClick={() => { item.onClick(); closeNow(); }}
              onKeyDown={(e) => handleItemKeyDown(e, i)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
                width: '100%', background: item.active ? 'rgba(245,166,35,0.1)' : 'none', border: 'none',
                cursor: 'pointer', padding: '9px 12px', borderRadius: '6px',
                fontSize: '0.84rem', fontWeight: 600, fontFamily: FONT_BODY, textAlign: 'left',
                color: item.active ? AMBER : 'rgba(255,255,255,0.75)',
              }}
            >
              {item.label}
              {item.badge && (
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: AMBER, flexShrink: 0 }} />
              )}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
